// The pieces every design is built from: the frame (where words may go in a
// feed post and in a Story), the family's type, the salon name and footer
// that frame every post, and a small layout engine.
//
// The engine lays a design's blocks down the frame. It measures them first,
// shrinks the big type together if they don't fit (small type never shrinks
// below LABEL, so it stays readable on a phone), then shares any spare space
// out where the design asks. One design therefore works in a 4:5 post and a
// 9:16 Story without being drawn twice.
import { dryPainter } from './painter'

// The smallest type on any post: 32px on a 1080px image is about 11.5pt in a
// phone's feed
export const LABEL = 32

// Words stay inside the frame. In a Story that's Meta's safe band: clear of
// the profile row at the top and the reply bar at the bottom.
export const frameFor = (format) => (format === 'story'
  ? { W: 1080, H: 1920, top: 300, bottom: 1236, left: 72, right: 1008, story: true }
  : { W: 1080, H: 1350, top: 68, bottom: 1290, left: 72, right: 1008, story: false })
export const widthOf = (fr) => fr.right - fr.left

// ─── Type ───
const fam = (env) => env.theme.family
// The salon's text size (Text tool) scales the big type only; small type
// stays at its readable minimum
export const display = (env, colour, size, extra = {}) => {
  const f = fam(env)
  const sized = size * ((env.theme && env.theme.size) || 1)
  return {
    font: f.display, weight: f.displayWeight, upper: f.upper, tracking: f.tracking, lh: f.lh,
    accentOn: !!f.italic, accentFont: f.italic, accentItalic: true, accentWeight: 400,
    color: colour, accentColor: colour,
    size: sized, minSize: Math.max(40, Math.round(sized * 0.5)),
    ...extra,
  }
}
// Big words set in the family's strong face (a poster's review quote): they
// follow the salon's headline font and size once they've chosen one
export const hero = (env, colour, size, extra = {}) => {
  const t = env.theme || {}
  const sized = size * (t.size || 1)
  const f = fam(env)
  const own = t.fontChosen
  return {
    font: own ? f.display : f.strong.font,
    weight: own ? f.displayWeight : f.strong.weight,
    accentOn: own && !!f.italic, accentFont: f.italic, accentItalic: true, accentWeight: 400,
    tracking: own ? f.tracking : -0.01,
    size: sized, minSize: Math.max(LABEL, Math.round(sized * 0.5)), lh: 1.14, color: colour, accentColor: colour,
    ...extra,
  }
}
export const label = (env, colour, extra = {}) => {
  const f = fam(env)
  return { font: f.label.font, weight: f.label.weight, upper: true, tracking: f.label.tracking, size: LABEL, minSize: 30, lh: 1.25, maxLines: 1, color: colour, ...extra }
}
export const body = (env, colour, size = 44, extra = {}) => {
  const f = fam(env)
  return { font: f.body.font, weight: f.body.weight, size: Math.max(LABEL, size), minSize: 30, lh: 1.32, color: colour, ...extra }
}
export const strong = (env, colour, size = 44, extra = {}) => {
  const f = fam(env)
  return { font: f.strong.font, weight: f.strong.weight, size: Math.max(LABEL, size), minSize: 30, lh: 1.16, color: colour, ...extra }
}

// ─── The engine ───
// A block: draw(painter, y, scale) draws it with its top at y and returns its
// bottom. gap is the space before it (it scales), flex its share of the spare
// space, and bottom: true puts it in the group resting on the frame's bottom.
const sum = (list) => list.reduce((a, v) => a + v, 0)
const gapsOf = (list, s) => list.reduce((a, b, i) => a + (i ? (b.gap || 0) * s : 0), 0)
export const measureFlow = (p, blocks, s = 1) => {
  const dp = p.isDry ? p : dryPainter(p)
  const list = blocks.filter(Boolean)
  const tops = list.filter(b => !b.bottom)
  const bots = list.filter(b => b.bottom)
  const th = tops.map(b => b.draw(dp, 0, s))
  const bh = bots.map(b => b.draw(dp, 0, s))
  const between = tops.length && bots.length ? (bots[0].gap || 0) * s : 0
  return { tops, bots, th, bh, need: sum(th) + sum(bh) + gapsOf(tops, s) + gapsOf(bots, s) + between }
}
export const flow = (p, fr, blocks, { min = 0.6 } = {}) => {
  const avail = fr.bottom - fr.top
  let s = 1
  let m = measureFlow(p, blocks, s)
  while (m.need > avail && s > min) {
    s = Math.max(min, Math.round((s - 0.04) * 100) / 100)
    m = measureFlow(p, blocks, s)
  }
  const spare = Math.max(0, avail - m.need)
  const flexTotal = sum(m.tops.map(b => b.flex || 0))
  let y = fr.top
  m.tops.forEach((b, i) => {
    if (i) y += (b.gap || 0) * s
    if (flexTotal) y += (spare * (b.flex || 0)) / flexTotal
    y = b.draw(p, y, s)
  })
  let yb = fr.bottom - (sum(m.bh) + gapsOf(m.bots, s))
  m.bots.forEach((b, i) => {
    if (i) yb += (b.gap || 0) * s
    yb = b.draw(p, yb, s)
  })
  return s
}

// A block of one piece of text
export const textBlock = (text, style, { gap = 0, flex = 0, slot } = {}) => (text ? {
  gap, flex,
  draw: (pp, y, s) => pp.text(text, { ...style(s), y, slot }).bottom,
} : null)

// ─── Discount codes on offers ───
// The code a client types, on a dashed ticket sized to it. Only a real,
// live code ever reaches a post (the studio's code picker), never typed.
export const codeTicket = (env, fr, colour, { gap = 34, align = 'left' } = {}) => {
  const code = env.post && env.post.content && env.post.content.code
  if (!code) return null
  return {
    gap,
    draw: (pp, y, s) => {
      const st = { ...strong(env, colour, Math.round(50 * Math.max(0.8, s))), tracking: 0.1, maxLines: 1 }
      const padX = 30
      const padY = 18
      const m = pp.text(code, { ...st, x: 0, y: 0, w: widthOf(fr) - padX * 2, dry: true })
      const w = Math.min(widthOf(fr), Math.ceil(m.width) + padX * 2)
      const h = Math.ceil(m.bottom - m.top) + padY * 2
      const x = align === 'center' ? fr.left + (widthOf(fr) - w) / 2 : fr.left
      const dash = [14, 10]
      pp.line(x, y, x + w, y, colour, 3, dash)
      pp.line(x + w, y, x + w, y + h, colour, 3, dash)
      pp.line(x + w, y + h, x, y + h, colour, 3, dash)
      pp.line(x, y + h, x, y, colour, 3, dash)
      pp.text(code, { ...st, x: x + padX, y: y + padY, w: w - padX * 2 })
      return y + h
    },
  }
}
// An offer's conditions (CAP Code 8.17: dates, who it's for, limits),
// written from the code and set small above the booking line
export const finePrint = (env, fr, colour, { gap = 28, align = 'left' } = {}) => {
  const t = env.post && env.post.content && env.post.content.small
  if (!t) return null
  return {
    bottom: true,
    gap,
    draw: (pp, y) => pp.text(t, { ...body(env, colour, LABEL), lh: 1.28, x: fr.left, y, w: widthOf(fr), maxLines: 4, align }).bottom,
  }
}

// ─── The frame every post wears ───
// The salon's name, or its logo, or neither (Text tool)
export const LOGO_H = 76
const logoOf = (env) => (env.theme.brandMode === 'logo' && env.theme.logo ? env.images[env.theme.logo] : null)
export const brand = (env, fr, colour, { gap = 0, align = 'left' } = {}) => {
  if (env.theme.brandMode === 'none') return null
  const img = logoOf(env)
  if (img) {
    return {
      gap,
      draw: (pp, y) => pp.logo(img, { x: fr.left, y, w: widthOf(fr), h: LOGO_H, align, tint: colour, slot: 'brand' }),
    }
  }
  return {
    gap,
    draw: (pp, y) => pp.text(env.theme.name, { ...label(env, colour), x: fr.left, y, w: widthOf(fr), align }).bottom,
  }
}
export const footer = (env, fr, colour, rule = colour, { text, align = 'left' } = {}) => {
  if (env.theme.hideCta && text == null) return null
  const t = text != null ? text : env.post.content.cta
  if (!t) return null
  return {
    bottom: true,
    gap: 40,
    draw: (pp, y) => {
      pp.line(fr.left, y + 1, fr.right, y + 1, rule, 2)
      return pp.text(t, { ...label(env, colour), x: fr.left, y: y + 28, w: widthOf(fr), maxLines: 2, align, slot: 'cta' }).bottom
    },
  }
}
// The salon's name (or logo) on a small block of colour, on a photo
export const nameTag = (p, env, x, y, bg, fg) => {
  if (env.theme.brandMode === 'none') return y
  const img = logoOf(env)
  if (!img) return tag(p, env, env.theme.name, x, y, bg, fg)
  const h = Math.round(LABEL * 1.25 + 26)
  const inner = h - 22
  const w = Math.min(420, Math.round((img.naturalWidth / img.naturalHeight) * inner)) + 44
  p.rect(x, y, w, h, bg)
  p.logo(img, { x: x + 22, y: y + 11, w: w - 44, h: inner, align: 'left', tint: fg })
  return y + h
}
// A label on a small block of colour, for sitting on a photo
export const tag = (p, env, text, x, y, bg, fg) => {
  const st = label(env, fg)
  const m = dryPainter(p).text(text, { ...st, x: 0, y: 0, w: 880 })
  const h = Math.round(LABEL * 1.25 + 26)
  p.rect(x, y, Math.min(936, m.width + 44), h, bg)
  p.text(text, { ...st, x: x + 22, y: y + 13, w: Math.min(892, m.width + 4) })
  return y + h
}

// ─── Photos ───
export const photoOf = (env, key) => {
  const ph = (env.post.photos || {})[key]
  return { ph: ph || {}, img: ph && ph.src ? env.images[ph.src] : null }
}
export const photo = (p, env, key, box) => {
  const { ph, img } = photoOf(env, key)
  p.image(img, { ...box, slot: `photo:${key}` }, ph, { bg: env.theme.c.holder, ink: env.theme.c.holderInk })
}

// A photo above a band of colour holding the words. In a feed post the photo
// takes the top photoH; in a Story the band is as tall as its words need
// (inside the safe band) and the photo fills everything above it.
export const photoBand = (p, env, { key, photoH, band, pad = 48, blocks }) => {
  const fr = env.frame
  if (!fr.story) {
    photo(p, env, key, { x: 0, y: 0, w: fr.W, h: photoH })
    p.rect(0, photoH, fr.W, fr.H - photoH, band)
    const f2 = { ...fr, top: photoH + pad }
    return flow(p, f2, blocks(f2))
  }
  const need = measureFlow(p, blocks(fr), 1).need
  const top = Math.max(700, Math.round(fr.bottom - need - pad))
  photo(p, env, key, { x: 0, y: 0, w: fr.W, h: top })
  p.rect(0, top, fr.W, fr.H - top, band)
  const f2 = { ...fr, top: top + pad }
  return flow(p, f2, blocks(f2))
}

// A Story with the photo filling the screen and the words on a card near the
// bottom of the safe band (never across a face at the top)
export const photoCard = (p, env, { key, card, radius = 0, inset = 48, pad = 52, blocks }) => {
  const fr = env.frame
  photo(p, env, key, { x: 0, y: 0, w: fr.W, h: fr.H })
  const cf = { ...fr, left: inset + pad, right: fr.W - inset - pad }
  const need = measureFlow(p, blocks(cf), 1).need
  const bottom = fr.bottom + 14
  const top = Math.max(720, Math.round(bottom - need - pad * 2))
  p.rect(inset, top, fr.W - inset * 2, bottom - top, card, radius)
  const f2 = { ...cf, top: top + pad, bottom: bottom - pad }
  return flow(p, f2, blocks(f2))
}

// Two matched photos for a before and after. Feed: side by side inside the
// frame. Story: side by side across the top of the screen, labelled inside
// the safe band, with the words below.
export const pairBlock = (env, fr, { h = 640, gap = 16, radius = 0, tagBg, tagFg, labelsBelow = false, labelColour } = {}) => ({
  gap: 40,
  draw: (pp, y, s) => {
    const w = (widthOf(fr) - gap) / 2
    const hh = Math.round(h * s)
    ;[['before', 'Before'], ['after', 'After']].forEach(([key, name], i) => {
      const x = fr.left + i * (w + gap)
      photo(pp, env, key, { x, y, w, h: hh, radius })
      if (!labelsBelow) tag(pp, env, name, x + 18, y + 18, tagBg, tagFg)
    })
    if (!labelsBelow) return y + hh
    let bottom = y + hh
    ;['Before', 'After'].forEach((name, i) => {
      const x = fr.left + i * (w + gap)
      bottom = Math.max(bottom, pp.text(name, { ...label(env, labelColour), x, y: y + hh + 16, w }).bottom)
    })
    return bottom
  },
})
export const pairStory = (p, env, { bg, tagBg, tagFg, pad = 48, blocks }) => {
  const fr = env.frame
  const need = measureFlow(p, blocks(fr), 1).need
  const top = Math.max(860, Math.round(fr.bottom - need - pad))
  p.rect(0, 0, fr.W, fr.H, bg)
  const w = (fr.W - 6) / 2
  ;[['before', 'Before'], ['after', 'After']].forEach(([key, name], i) => {
    const x = i * (w + 6)
    photo(p, env, key, { x, y: 0, w, h: top })
    tag(p, env, name, x + (i ? 24 : fr.left), fr.top + 12, tagBg, tagFg)
  })
  const f2 = { ...fr, top: top + pad }
  return flow(p, f2, blocks(f2))
}

// ─── Small parts ───
// Time chips: up to three across
export const pills = (env, fr, items, { bg, fg, h = 104, gap = 22, radius = 999 } = {}) => ({
  gap: 28,
  draw: (pp, y) => {
    const list = items.slice(0, 3)
    const w = (widthOf(fr) - gap * 2) / 3
    list.forEach((t, i) => {
      const x = fr.left + i * (w + gap)
      pp.rect(x, y, w, h, bg, radius)
      pp.text(t, { ...strong(env, fg, 40), x: x + 14, y: y + (h - 40 * 1.16) / 2, w: w - 28, align: 'center', maxLines: 1, slot: 'times' })
    })
    return y + (list.length ? h : 0)
  },
})
// A panel of colour sized to what's inside it
export const panel = (pp, { x, y, w, bg, pad = 36, radius = 0, content }) => {
  const dp = pp.isDry ? pp : dryPainter(pp)
  const h = content(dp, y + pad) - y + pad
  pp.rect(x, y, w, h, bg, radius)
  content(pp, y + pad)
  return y + h
}
export const rule = (fr, colour, { gap = 44, width = 2 } = {}) => ({
  gap,
  draw: (pp, y) => { pp.line(fr.left, y + width / 2, fr.right, y + width / 2, colour, width); return y + width },
})
